import React, { CSSProperties, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PricingRecordAction } from "./pricingRecordReducer";
import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import Label from "@components/inputs/text/Label";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import DatePicker from "@components/inputs/DateInput";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import DropDownMenu from "@components/inputs/DropDownMenu";
import { dateToISOFormat, addDays } from "@utils/misc/functions";
import { PlanType } from "@utils/quotePlanOptions";
import { Country } from "@utils/constants/localisation";
import { countrySpecificCurrency } from "@utils/misc/functions";

interface VehicleDetailsViewConfig {
    // List of all the fields for a given pricing_record.
    // Each includedField is the name of a field in the reducer's IPricingRecord.
    // There should be a matching input in the outpout for each one.
    includedFields: (keyof IPricingRecord)[];

    // List of required fields for a given pricing_record.
    // Used for validation to ensure all necessary fields are filled.
    requiredFields: (keyof IPricingRecord)[];

    // List of disabled fields for a given pricing_record.
    disabledFields: (keyof IPricingRecord)[];
}

const DEFAULT_VIEW_CONFIG: VehicleDetailsViewConfig = {
    includedFields: [],
    requiredFields: [],
    disabledFields: []
}

export const VIEW_CONFIG: Record<PlanType, { [vehicle_condition: string]: VehicleDetailsViewConfig }> = {
  [PlanType.FLEXI_OWN]: {
    'new': {
      includedFields: ['setup_fee_including_gst', 'vehicle_colour', 'vehicle_delivery_eta'],
      requiredFields: ['setup_fee_including_gst', 'vehicle_colour', 'vehicle_delivery_eta'],
      disabledFields: []
    },

    'preown': {
      includedFields: ['setup_fee_including_gst', 'vehicle_registration', 'vehicle_driveaway_price', 'term_reduction', 'vehicle_colour', 'vehicle_delivery_eta', 'replacement_tyres', 'replacement_brake_pad_set', 'replacement_or_machining_of_brake_discs'],
      requiredFields: ['setup_fee_including_gst', 'vehicle_registration', 'vehicle_driveaway_price', 'term_reduction', 'vehicle_colour', 'vehicle_delivery_eta', 'replacement_tyres', 'replacement_brake_pad_set', 'replacement_or_machining_of_brake_discs'],
      disabledFields: []
    }
  },

  [PlanType.FLEXI_TRY]: {
    'na': {
      includedFields: ['setup_fee_including_gst', 'vehicle_delivery_eta'],
      requiredFields: ['setup_fee_including_gst', 'vehicle_delivery_eta'],
      disabledFields: []
    }
  }
} 

type Props = {
  pricingRecordState: IPricingRecord | null;
  pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
  navigateLandingPageTarget: string;
  navigateNextTarget: string;
};

export default function VehicleDetailsView({
  pricingRecordState,
  pricingRecordDispatch,
  navigateLandingPageTarget,
  navigateNextTarget,
}: Props) {
  const navigate = useNavigate();

  const country = pricingRecordState?.country as Country;
  const currency = countrySpecificCurrency(country);

  const PLAN = pricingRecordState?.plan as PlanType;
  const VEHICLE_CONDITION = pricingRecordState?.vehicle_condition;
  const CONFIG = PLAN && VEHICLE_CONDITION ?
          VIEW_CONFIG[PLAN][VEHICLE_CONDITION] :
          DEFAULT_VIEW_CONFIG;

  const { includedFields, requiredFields } = CONFIG ? CONFIG : DEFAULT_VIEW_CONFIG

  // Create a copy of disabledFields to avoid mutating the shared config
  const disabledFields = [...(CONFIG?.disabledFields || DEFAULT_VIEW_CONFIG.disabledFields)]

  // Flexi_Temp only applies to Flexi Try
  if (pricingRecordState?.plan === PlanType.FLEXI_TRY && pricingRecordState?.zoho_potential?.Flexi_Temp === 'Yes') {
    disabledFields.push("setup_fee_including_gst")
  }

  useEffect(() => {
    if (pricingRecordState === null) {
      navigate(navigateLandingPageTarget);
    }
  }, [pricingRecordState, navigateLandingPageTarget, navigate]);

  useEffect(() => {
    // This will scroll to top for long forms like FO Preown on mount of page
    window.scrollTo(0, 0)
  }, [])
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    pricingRecordDispatch({
      type: "UPDATE_FIELD",
      payload: {
        field: e.target.name,
        value: e.target.value,
      },
    });
  };

  const handleDropDownIntValueChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedValue = event.currentTarget.value;
    pricingRecordDispatch({
      type: "UPDATE_FIELD",
      payload: {
        field: event.target.name,
        value: parseInt(selectedValue),
      },
    });
  };

  const allowIntOnly = (event: any) => {
    if (
      !/[0-9]|Backspace|Tab|Enter|Delete|ArrowLeft|ArrowRight/.test(event.key)
    ) {
      event.preventDefault();
    }
  };

  const preventScrollIncrement = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };

  const textBoxStyle: CSSProperties = {
    height: "5vh",
    borderRadius: "5px",
    border: "1px solid #C4C4C4",
    fontSize: "18px",
    justifyContent: "left",
    margin: "1vh 0vw",
    boxSizing: "border-box",
    width: "50%",
    left: "28px",
  };

  /**
   * On submit handler
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(navigateNextTarget);
  };

  return (
    <>
      <NavBar pageTitle="Vehicle Details & Fees" />
      <form style={{ margin: "2.5vh 2.5vw" }} onSubmit={handleSubmit}>
        {pricingRecordState &&
          pricingRecordState.vehicle_condition != "new" && (
            <p>
              Please use the Pre-owned calculator and Fleetio
              to populate the following Pre-owned options:
            </p>
          )}
        {
          includedFields.includes('setup_fee_including_gst') && 
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label text={"Setup fee (incl TAX)"} requiredFieldInd={requiredFields.includes('setup_fee_including_gst')} />
            <DropDownMenu
              menuName="setup_fee_including_gst"
              required={requiredFields.includes('setup_fee_including_gst')}
              choices={
                (pricingRecordState?.reference_values?.setup_fee_including_gst?.fixed_values || []).map((item: number) => ({
                  label: `${item}`,
                  value: `${item}`,
                  enabled: true,
                })) 
              }
              
              defaultVal={
                pricingRecordState?.setup_fee_including_gst || pricingRecordState?.setup_fee_including_gst === 0
                  ? {
                    value:
                      pricingRecordState?.setup_fee_including_gst.toString(),
                  }
                  : undefined
              }
              onSelect={handleDropDownIntValueChange}
              disabled={disabledFields.includes('setup_fee_including_gst')}
              testId="setupFee"
            />
          </div>
        }

        {
          (
            includedFields.includes('vehicle_registration') || 
            includedFields.includes('vehicle_driveaway_price') ||
            includedFields.includes('term_reduction') || 
            includedFields.includes('vehicle_colour') || 
            includedFields.includes('vehicle_delivery_eta')
          ) && 
          <Label text="Vehicle Details" styleCfg={{ bold: true }} />
        }

        {
          includedFields.includes('vehicle_registration') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label text={"Vehicle Registration"} requiredFieldInd={requiredFields.includes('vehicle_registration')} />
            <input
              style={textBoxStyle}
              name="vehicle_registration"
              defaultValue={
                pricingRecordState?.vehicle_registration
                  ? pricingRecordState?.vehicle_registration
                  : undefined
              }
              required={requiredFields.includes('vehicle_registration')}
              onChange={handleChange}
              maxLength={pricingRecordState?.reference_values?.vehicle_registration?.max_chars}
              disabled={disabledFields.includes('vehicle_registration')}
              data-testid="vehicleRegistration"
            />
          </div>
        }

        {
          includedFields.includes('vehicle_driveaway_price') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label
              text={`Vehicle Driveaway Price (${currency} incl TAX)`}
              requiredFieldInd={requiredFields.includes('vehicle_driveaway_price')}
            />
            <input
              style={textBoxStyle}
              name="vehicle_driveaway_price"
              defaultValue={
                pricingRecordState?.vehicle_driveaway_price
                  ? pricingRecordState?.vehicle_driveaway_price
                  : undefined
              }
              required={requiredFields.includes('vehicle_driveaway_price')}
              onChange={handleChange}
              type="number"
              onWheel={preventScrollIncrement}
              min={pricingRecordState?.reference_values?.vehicle_driveaway_price?.min_value}
              max={pricingRecordState?.reference_values?.vehicle_driveaway_price?.max_value}
              disabled={disabledFields.includes('vehicle_driveaway_price')}
              data-testid="vehicleDriveawayPrice"
            />
          </div>
        }

        {
          includedFields.includes('term_reduction') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label text={"Term Reduction"} requiredFieldInd={requiredFields.includes('term_reduction')} />
            <input
              style={textBoxStyle}
              name="term_reduction"
              defaultValue={
                pricingRecordState?.term_reduction
                  ? pricingRecordState?.term_reduction
                  : undefined
              }
              required={requiredFields.includes('term_reduction')}
              onChange={handleChange}
              type="number"
              onKeyDown={allowIntOnly}
              onWheel={preventScrollIncrement}
              min={pricingRecordState?.reference_values?.term_reduction?.min_value}
              max={pricingRecordState?.reference_values?.term_reduction?.max_value}
              disabled={disabledFields.includes('term_reduction')}
              data-testid="termReduction"
            />
          </div>
        }

        {
          includedFields.includes('vehicle_colour') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label text={"Vehicle Colour"} requiredFieldInd={requiredFields.includes('vehicle_colour')} />
            <input
              style={textBoxStyle}
              name="vehicle_colour"
              defaultValue={
                pricingRecordState?.vehicle_colour
                  ? pricingRecordState?.vehicle_colour
                  : undefined
              }
              required={requiredFields.includes('vehicle_colour')}
              onChange={handleChange}
              maxLength={pricingRecordState?.reference_values?.vehicle_colour?.max_chars}
              disabled={disabledFields.includes('vehicle_colour')}
              data-testid="vehicleColour"
            />
          </div>
        }

        {
          includedFields.includes('vehicle_delivery_eta') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label text={"Delivery ETA"} requiredFieldInd={requiredFields.includes('vehicle_delivery_eta')} />
            <DatePicker
              name="vehicle_delivery_eta"
              defaultVal={
                pricingRecordState?.vehicle_delivery_eta
                  ? pricingRecordState?.vehicle_delivery_eta
                  : undefined
              }
              required={requiredFields.includes('vehicle_delivery_eta')}
              onChange={handleChange}
              min={
                    (
                      pricingRecordState?.reference_values?.vehicle_delivery_eta?.date_offset_lower ||
                      pricingRecordState?.reference_values?.vehicle_delivery_eta?.date_offset_lower === 0
                    )
                    &&
                    pricingRecordState?.created_datetime
                    ? dateToISOFormat(addDays(new Date(pricingRecordState?.created_datetime), pricingRecordState?.reference_values?.vehicle_delivery_eta?.date_offset_lower))
                    : undefined
                }
              max={
                    (
                      pricingRecordState?.reference_values?.vehicle_delivery_eta?.date_offset_upper ||
                      pricingRecordState?.reference_values?.vehicle_delivery_eta?.date_offset_upper === 0
                    )
                    &&
                    pricingRecordState?.created_datetime
                    ? dateToISOFormat(addDays(new Date(pricingRecordState?.created_datetime), pricingRecordState?.reference_values?.vehicle_delivery_eta?.date_offset_upper))
                    : undefined
                }
              disabled={disabledFields.includes('vehicle_delivery_eta')}
            />
          </div>
        }
        
        {
          (
            includedFields.includes('replacement_tyres') || 
            includedFields.includes('replacement_brake_pad_set') ||
            includedFields.includes('replacement_or_machining_of_brake_discs')
          ) && 
          <Label text="Maintenance Limits" styleCfg={{ bold: true }} />
        }

        {
          includedFields.includes('replacement_tyres') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label text={"Replacement tyres"} requiredFieldInd={requiredFields.includes('replacement_tyres')} />
            <DropDownMenu
              menuName="replacement_tyres"
              required={requiredFields.includes('replacement_tyres')}
              choices={(pricingRecordState?.reference_values?.replacement_tyres?.fixed_values || []).map((item: number) => ({
                label: `${item}`,
                value: `${item}`,
                enabled: true,
              }))}
              defaultVal={
                pricingRecordState?.replacement_tyres || pricingRecordState?.replacement_tyres === 0
                  ? {
                    value:
                      pricingRecordState?.replacement_tyres.toString(),
                  }
                  : undefined
              }
              onSelect={handleDropDownIntValueChange}
              disabled={disabledFields.includes('replacement_tyres')}
              testId="replacementTyres"
            />
          </div>
        }

        {
          includedFields.includes('replacement_brake_pad_set') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label
              text={"Replacement brake pad set"}
              requiredFieldInd={requiredFields.includes('replacement_brake_pad_set')}
            />
            <DropDownMenu
              menuName="replacement_brake_pad_set"
              required={requiredFields.includes('replacement_brake_pad_set')}
              choices={(pricingRecordState?.reference_values?.replacement_brake_pad_set?.fixed_values || []).map((item: number) => ({
                label: `${item}`,
                value: `${item}`,
                enabled: true,
              }))}
              defaultVal={
                pricingRecordState?.replacement_brake_pad_set || pricingRecordState?.replacement_brake_pad_set === 0
                  ? {
                    value:
                      pricingRecordState?.replacement_brake_pad_set.toString(),
                  }
                  : undefined
              }
              onSelect={handleDropDownIntValueChange}
              disabled={disabledFields.includes('replacement_brake_pad_set')}
              testId="replacementBrakePadSet"
            />
          </div>
        }

        {
          includedFields.includes('replacement_or_machining_of_brake_discs') &&
          <div style={{ margin: "2.5vh 0vw" }}>
            <Label
              text={"Replacement or machining of brake discs"}
              requiredFieldInd={requiredFields.includes('replacement_or_machining_of_brake_discs')}
            />
            <DropDownMenu
              menuName="replacement_or_machining_of_brake_discs"
              required={requiredFields.includes('replacement_or_machining_of_brake_discs')}
              choices={(pricingRecordState?.reference_values?.replacement_or_machining_of_brake_discs?.fixed_values || []).map((item: number) => ({
                label: `${item}`,
                value: `${item}`,
                enabled: true,
              }))}
              defaultVal={
                pricingRecordState?.replacement_or_machining_of_brake_discs || pricingRecordState?.replacement_or_machining_of_brake_discs === 0
                  ? {
                    value:
                      pricingRecordState?.replacement_or_machining_of_brake_discs.toString(),
                  }
                  : undefined
              }
              onSelect={handleDropDownIntValueChange}
              disabled={disabledFields.includes('replacement_or_machining_of_brake_discs')}
              testId="replacementOrMachiningOfBrakeDiscs"
            />
          </div>
        }

        <div style={{ margin: "2.5vh 0" }}>
          <SubmitBtn text="Next" />
        </div>
      </form>
    </>
  );
}
